import React from 'react';
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Lightbulb,
  Loader2,
  Trash2,
  Wand2,
} from 'lucide-react';
import { t } from '../../i18n';

import ArrowScrollRow from '../../components/common/ArrowScrollRow';
/**
 * Geteilte Bausteine für ALLE Bearbeiten-Seiten (Turnierserie, Verein,
 * Shop, Spielstätte, Liga/Verband).
 *
 * Apple-Stil (Welle 1, Marcel 2026-09-24/25, Blaupause
 * Analysen/2026-09-24-erstellen-verwalten-apple-stil-blaupause.md):
 *   Kopf: runder Ghost-Zurück, Kapitälchen-Zeile türkis, großer enger Name,
 *         rechts Aktionen als runde Ghost-Icons – kein Kasten, keine Pillen
 *   Sichtbarkeit: schlichte Fortschrittszeile, aufklappbar, Trennlinien
 *   Bereiche: flache Unterstrich-Tabs, sticky unter der Navbar
 *   Speichern: Save-Bar unten mit EINEM türkisen Primärbutton
 *
 * Props-Verträge sind unverändert (nur optionale Erweiterungen), damit die
 * fünf Seiten den neuen Look ohne Änderung bekommen. Feld-Bausteine für die
 * Inhalte liegen in formParts.tsx.
 */

export const TEAL = '#3fced1';

/** Runder Ghost-Icon-Button (Zurück/Aktionen) – gleiche Bauart wie die Detailseiten. */
/** Runder Ghost-Icon-Button im Seitenkopf (h-10) – auch von manageParts/SeriesEventsPage genutzt */
export const GHOST_ICON =
  'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-zinc-300 transition hover:bg-zinc-900 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60';

// ============================================
// Sichtbarkeits-Score
// ============================================

export interface QualityCriterion {
  label: string;
  tip: string;
  points: number;
  met: boolean;
}

export interface QualityScore {
  criteria: QualityCriterion[];
  percentage: number;
  label: string;
  color: string;
  met: QualityCriterion[];
  unmet: QualityCriterion[];
  metCount: number;
}

/** Punkte → Prozent/Label/Farbe, identische Schwellen wie Turnier + App. */
export function buildQualityScore(criteria: QualityCriterion[]): QualityScore {
  const max = criteria.reduce((s, c) => s + c.points, 0);
  const achieved = criteria.filter((c) => c.met).reduce((s, c) => s + c.points, 0);
  const percentage = max > 0 ? Math.round((achieved / max) * 100) : 0;
  const label =
    percentage >= 85 ? t('web.component.edit-shared.ausgezeichnet')
      : percentage >= 70 ? t('web.component.edit-shared.gut')
      : percentage >= 50 ? t('web.component.edit-shared.ausbaufaehig')
      : percentage >= 30 ? t('web.component.edit-shared.verbesserungswuerdig')
      : t('web.component.edit-shared.unvollstaendig');
  const color = percentage >= 30 ? TEAL : '#6B7280';
  return {
    criteria,
    percentage,
    label,
    color,
    met: criteria.filter((c) => c.met),
    unmet: criteria.filter((c) => !c.met),
    metCount: criteria.filter((c) => c.met).length,
  };
}

/** Sichtbarkeit als schlichte Fortschrittszeile: Kapitälchen, Prozent, dünner Balken; aufklappbar. */
export const QualityScoreCard: React.FC<{
  score: QualityScore;
  expanded: boolean;
  onToggle: () => void;
}> = ({ score, expanded, onToggle }) => (
  <section className="border-b border-zinc-800" data-testid="quality-score">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="flex w-full items-center gap-4 py-4 text-left focus-visible:outline-none"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: TEAL }}>
            {t('web.component.edit-shared.sichtbarkeits-score')}
          </p>
          <p className="text-sm font-semibold tabular-nums text-white">
            <span data-testid="quality-score-percent">{score.percentage}%</span>
            <span className="ml-2 font-medium text-zinc-400">{score.label}</span>
          </p>
        </div>
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-zinc-800" aria-hidden="true">
          <div className="h-full rounded-full transition-all" style={{ width: `${score.percentage}%`, backgroundColor: score.color }} data-testid="quality-score-bar" />
        </div>
      </div>
      <span className="shrink-0 text-xs font-medium tabular-nums text-zinc-400">
        {score.metCount}/{score.criteria.length}
      </span>
      <ChevronDown size={18} className={`shrink-0 text-zinc-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
    </button>
    {expanded && (
      <div className="pb-5" data-testid="quality-score-details">
        {score.met.length > 0 && (
          <div className="mb-5">
            <p className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
              <CheckCircle2 size={14} style={{ color: TEAL }} />
              {t('web.component.edit-shared.erfuellt', { metCount: score.metCount, length: score.criteria.length })}
            </p>
            <ul className="divide-y divide-zinc-800">
              {score.met.map((c) => (
                <li key={c.label} className="flex items-center gap-3 py-2.5 text-sm text-zinc-200">
                  <Check size={14} style={{ color: TEAL }} className="shrink-0" />
                  <span>{c.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {score.unmet.length > 0 && (
          <div>
            <p className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
              <Lightbulb size={14} style={{ color: TEAL }} />
              {t('web.component.edit-shared.verbesserungspotenzial')}
            </p>
            <ul className="divide-y divide-zinc-800">
              {score.unmet.map((c) => (
                <li key={c.label} className="flex items-start gap-3 py-3">
                  <span className="shrink-0 pt-0.5 text-xs font-semibold tabular-nums" style={{ color: TEAL }}>+{c.points}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white">{c.label}</p>
                    <p className="text-[13px] leading-relaxed text-zinc-400">{c.tip}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )}
  </section>
);

// ============================================
// Bereichs-Tabs (Unterstrich, sticky unter der Navbar)
// ============================================

export interface EditSection {
  key: string;
  label: string;
  /** optional – in den Unterstrich-Tabs NICHT gerendert (Marcel 2026-09-24: keine Icons in Tabs) */
  icon?: React.ComponentType<{ size?: number }>;
  /** optionaler Zähler hinter dem Label (z. B. Anzahl Mannschaften) */
  count?: number;
}

export const EditSectionTabs: React.FC<{
  sections: EditSection[];
  active: string;
  onSelect: (key: string) => void;
  /** Standard: klebt unter der Navbar (top-16). false = im Fluss. */
  sticky?: boolean;
  /** 'underline' (Standard, Haupt-Tabs) oder 'chips' (Unter-Tabs, Marcel 2026-10-08: hebt sich von der oberen Leiste ab) */
  variant?: 'underline' | 'chips';
  /** Chip-Variante: aria-label der Leiste (role=tablist) und testId-Präfix der Pfeile (Standard 'edit-tabs') */
  ariaLabel?: string;
  testId?: string;
  className?: string;
}> = ({ sections, active, onSelect, sticky = true, variant = 'underline', ariaLabel, testId = 'edit-tabs', className = '' }) => {
  if (variant === 'chips') {
    return (
      <div className={`-mx-4 px-4 sm:mx-0 sm:px-0 ${className}`.trim()} data-testid="edit-section-tabs" data-variant="chips">
        <ArrowScrollRow testId={testId} role="tablist" ariaLabel={ariaLabel} scrollClassName="flex gap-2 py-1">
          {sections.map((s) => {
            const selected = active === s.key;
            return (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={selected}
                data-testid={`edit-tab-${s.key}`}
                onClick={() => onSelect(s.key)}
                className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition-colors ${
                  selected ? 'border-[#3fced1] bg-[#3fced1] text-black' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white'
                }`}
              >
                <span>{s.label}</span>
                {typeof s.count === 'number' && s.count > 0 && (
                  <span className={`text-[12px] ${selected ? 'text-black/70' : 'text-zinc-500'}`}>{s.count}</span>
                )}
              </button>
            );
          })}
        </ArrowScrollRow>
      </div>
    );
  }
  return (
    <div
      className={`${sticky ? 'sticky top-16 z-20' : ''} -mx-4 border-b border-zinc-800 bg-black/90 px-4 backdrop-blur-md sm:mx-0 sm:px-0`}
      data-testid="edit-section-tabs"
      data-variant="underline"
    >
      {/* Apple-Stil (Marcel 2026-09-24): reine Unterstrich-Tabs – keine Icons, kein
          Kasten; Pfeile links/rechts IMMER sichtbar (ArrowScrollRow), damit klar ist,
          dass rechts weitere Tabs liegen */}
      <ArrowScrollRow testId="edit-tabs" role="tablist" scrollClassName="flex gap-7">
        {sections.map((s) => {
          const selected = active === s.key;
          return (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={selected}
              data-testid={`edit-tab-${s.key}`}
              onClick={() => onSelect(s.key)}
              className={`relative flex shrink-0 items-center gap-1.5 whitespace-nowrap py-3.5 text-[15px] transition-colors ${
                selected ? 'font-semibold text-white' : 'font-medium text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <span>{s.label}</span>
              {typeof s.count === 'number' && s.count > 0 && (
                <span className={`text-[13px] ${selected ? 'text-zinc-400' : 'text-zinc-600'}`}>{s.count}</span>
              )}
              {selected && (
                <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-[#3fced1]" data-testid="edit-tab-underline" />
              )}
            </button>
          );
        })}
      </ArrowScrollRow>
    </div>
  );
};

// ============================================
// Kopf + Save-Bar
// ============================================

export const EditPageHeader: React.FC<{
  title: string;
  name: string;
  onBack: () => void;
  onDelete?: () => void;
  /** Einrichtungs-Guide öffnen (Marcel 2026-09-21) – Zauberstab rechts im Kopf */
  onGuide?: () => void;
  changed?: boolean;
  cancelled?: boolean;
}> = ({ title, name, onBack, onDelete, onGuide, changed, cancelled }) => (
  <header className="mx-auto max-w-3xl px-4 pt-4 sm:px-6 sm:pt-6" data-testid="edit-page-header">
    <div className="flex items-center justify-between gap-3">
      <button type="button" onClick={onBack} aria-label={t('web.component.chats-manager.zurueck')} className={GHOST_ICON}>
        <ArrowLeft size={20} />
      </button>
      <div className="flex items-center gap-1">
        {onGuide && (
          <button
            type="button"
            onClick={onGuide}
            title={t('web.page.listing-setup-guide.guide-oeffnen')}
            aria-label={t('web.page.listing-setup-guide.guide-oeffnen')}
            className={GHOST_ICON}
            style={{ color: TEAL }}
            data-testid="edit-header-guide"
          >
            <Wand2 size={19} />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            title={t('web.component.comment-item.loeschen')}
            aria-label={t('web.component.comment-item.loeschen')}
            className={`${GHOST_ICON} text-red-500 hover:text-red-400`}
            data-testid="edit-header-delete"
          >
            <Trash2 size={19} />
          </button>
        )}
      </div>
    </div>

    {/* Kapitälchen-Zeile: Entität · Zustand (Textinhalt bleibt, Kapitälchen per CSS) */}
    <p className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.18em]" data-testid="edit-header-eyebrow">
      <span style={{ color: TEAL }}>{title}</span>
      {cancelled && <span className="text-red-400" data-testid="edit-header-cancelled">{t('web.component.edit-shared.abgesagt')}</span>}
      {changed && <span className="text-amber-400" data-testid="edit-header-changed">{t('web.component.edit-shared.ungespeichert')}</span>}
    </p>
    <h1 className="mt-2 break-words text-3xl font-semibold leading-[1.1] tracking-tight text-white sm:text-4xl">{name}</h1>
    <div className="mt-6 border-b border-zinc-800 sm:mt-8" />
  </header>
);

/** Hinweis-Zeile „Einrichtungs-Guide“: eine Zeile mit Icon, Text und Chevron, Trennlinie – kein Kasten. */
export const GuideCallout: React.FC<{ onOpen: () => void }> = ({ onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="flex w-full items-center gap-4 border-b border-zinc-800 py-4 text-left transition hover:bg-zinc-900/40 focus-visible:outline-none"
    data-testid="guide-callout"
  >
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#3fced1]/10" style={{ color: TEAL }}>
      <Wand2 size={17} />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold text-white">{t('web.page.listing-setup-guide.guide-oeffnen')}</span>
      <span className="block text-[13px] leading-snug text-zinc-400">{t('web.page.listing-setup-guide.guide-hinweis')}</span>
    </span>
    <ChevronRight size={18} className="shrink-0 text-zinc-500" />
  </button>
);

export const StickySaveBar: React.FC<{
  saving: boolean;
  onSave: () => void;
  label?: string;
  /** Optional: Sekundär-Aktion als Text-Button links (z. B. Verwerfen). */
  secondaryLabel?: string;
  onSecondary?: () => void;
  /** Optional: nichts geändert → Primärbutton deaktiviert. */
  disabled?: boolean;
}> = ({
  saving,
  onSave,
  label = t('web.component.competition-edit-modal.aenderungen-speichern'),
  secondaryLabel,
  onSecondary,
  disabled = false,
}) => (
  <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-zinc-800 bg-zinc-950/90 backdrop-blur" data-testid="sticky-save-bar">
    <div className="mx-auto flex max-w-3xl items-center justify-end gap-3 px-4 py-3 sm:px-6">
      {secondaryLabel && onSecondary && (
        <button
          type="button"
          onClick={onSecondary}
          disabled={saving}
          className="rounded-full px-4 py-2.5 text-sm font-semibold text-zinc-300 transition hover:text-white disabled:opacity-50"
        >
          {secondaryLabel}
        </button>
      )}
      <button
        type="button"
        onClick={onSave}
        disabled={saving || disabled}
        aria-busy={saving || undefined}
        className="flex items-center justify-center gap-2 rounded-full px-7 py-3 text-sm font-semibold text-black transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
        style={{ backgroundColor: TEAL }}
        data-testid="sticky-save-button"
      >
        {saving && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
        {label}
      </button>
    </div>
  </div>
);

/** Primärbutton IM Formular, volle Breite – Marcel 2026-09-26: „der Erstellen-Button
 *  sollte unter ‚Weitere Details wie …‘ sein, sonst finden ihn viele nicht“. Ergänzt
 *  die StickySaveBar (die auf Mobile am Seitenende leicht übersehen wird). */
export const InlineSubmitButton: React.FC<{
  saving: boolean;
  onClick: () => void;
  label: string;
  disabled?: boolean;
  testId?: string;
}> = ({ saving, onClick, label, disabled = false, testId = 'inline-submit-button' }) => (
  <div className="pt-2" data-testid={`${testId}-row`}>
    <button
      type="button"
      onClick={onClick}
      disabled={saving || disabled}
      aria-busy={saving || undefined}
      className="flex w-full items-center justify-center gap-2 rounded-full px-7 py-3.5 text-[15px] font-semibold text-black transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
      style={{ backgroundColor: TEAL }}
      data-testid={testId}
    >
      {saving && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
      {label}
    </button>
  </div>
);

/** Sektions-Überschrift innerhalb eines Tabs: Kapitälchen + optionaler Hinweis. */
export const SectionHeading: React.FC<{
  icon: React.ComponentType<{ size?: number }>;
  title: string;
  hint?: string;
}> = ({ icon: HIcon, title, hint }) => (
  <div className="mb-4">
    <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
      <span style={{ color: TEAL }}><HIcon size={14} /></span> {title}
    </p>
    {hint && <p className="mt-1 text-[13px] leading-relaxed text-zinc-500">{hint}</p>}
  </div>
);
